/**
 * +field / +zone: Mossvale's open map (docs/design/zones.md "Mossvale"). Phase 1's town, Sunmeadow
 * and Bramblewood (three small maps joined by portals) are merged into one seamless zone,
 * mossvaleZone.ts; this module keeps the shared field data (solo auras, ground loot), the start
 * point and the list of open maps.
 *
 * Coordinates are yards (x east, y north = away from the default camera).
 */
import type { AuraDef, DungeonDef, FieldDef, PortalDef, Vec2 } from '@keystone/sim';
import { angleTo } from '../dungeons/geometry';
import { FIELD_AURA, MAP, MOB } from './ids';
import { ZONE_SPOTS, buildMossvaleZone } from './mossvaleZone';
import type { StartPoint } from './types';

// ---------------------------------------------------------------------------
// Field-only party auras (solo balance)
// ---------------------------------------------------------------------------

/**
 * Solo damage boost for the support roles while grinding (FieldDef.partyAuras). A global
 * damageDonePct mod (no school/ability filter), so it multiplies every hit the hero lands.
 * At L1 a Lightbinder spamming Judgment + Holy Shock does ≈ 2k DPS (L10 units) and a Warden
 * ≈ 2.6k vs ≈ 3k for a DPS hero's core kit: +100% / +10% (the Warden's no-rest sustain does the rest) puts both at a fair pace without
 * out-damaging the DPS classes once the healer spends GCDs on self-heals.
 */
export const FIELD_AURAS: AuraDef[] = [
  {
    id: FIELD_AURA.soloTank,
    name: "Wayfarer's Might",
    kind: 'buff',
    durationMs: 0,
    stacking: 'unique',
    // Grind harness (competent bots, time to 10): +40% → 37 min, +22% → 43 min, +0% → 48 min vs
    // 68–83 min for the others. The Warden never needs to rest, so the boost is not what sets its
    // pace; it stays a token +10%.
    // +classes: the base Warrior (Hew + Breaker + Wide Arc) grinds at DPS pace on its own (44 min
    // to 10 at +10%), so the boost is a token +5%; the aura stays as the hook for later specs.
    mods: [{ stat: 'damageDonePct', value: 0.05 }],
    flags: { persistThroughDeath: true },
  },
  {
    id: FIELD_AURA.soloHealer,
    name: "Wayfarer's Zeal",
    kind: 'buff',
    durationMs: 0,
    stacking: 'unique',
    // +classes: the base Acolyte's Lightlash + Sanctum Circle are real damage: +100% made it the
    // fastest leveller (36 min to 10); +25% puts it with the damage dealers. +zone: on the open zone (more walking
    // between spread-out singles) +40% keeps it within ~10% of the other bases (83 vs ~80 min to 10).
    mods: [{ stat: 'damageDonePct', value: 0.4 }],
    flags: { persistThroughDeath: true },
  },
];

const SOLO_AURAS: FieldDef['partyAuras'] = { tank: FIELD_AURA.soloTank, healer: FIELD_AURA.soloHealer };

/**
 * Ground loot (sim field/loot.ts; docs/design/zones.md "Pickups"): gold piles from 30% of kills,
 * a gear drop from 2.5% (elites always), XP wisp rings every 60–90 s, a coin pouch every 60–90 s,
 * a treasure chest every 2.5–3.5 min and the treasure sprite every 4–6 min. Sized so pickups are
 * ≈ 20% of an active player's XP (zones.md table).
 */
const FIELD_LOOT: FieldDef['loot'] = {
  killGoldChance: 0.3,
  killItemChance: 0.025,
  wispEveryMs: [60_000, 90_000],
  pouchEveryMs: [60_000, 90_000],
  chestEveryMs: [150_000, 210_000],
  spriteEveryMs: [240_000, 360_000],
  spriteNpcId: MOB.treasureSprite,
};

const r1 = (v: number): number => Math.round(v * 10) / 10;
const r3 = (v: number): number => Math.round(v * 1000) / 1000;

/** Where a hero steps out of `p` on arrival (in front of its mouth). Renderers / the sim may use it. */
export function portalArrival(p: PortalDef, clearance = 3): Vec2 {
  return { x: r1(p.pos.x + Math.cos(p.facing) * (p.radius + clearance)), y: r1(p.pos.y + Math.sin(p.facing) * (p.radius + clearance)) };
}

/** +zone: the whole Mossvale zone (town, Sunmeadow, Glimmermere, Greyhorn Heights, Bramblewood, the Pale Rise). */
export const mossvale: DungeonDef = buildMossvaleZone({ partyAuras: SOLO_AURAS, loot: FIELD_LOOT });

/** Where a new character appears (Mossvale town, in front of Elder Rowan). */
/** +spawn: new characters start at Granny Pell's cottage, facing the Tower across the valley. */
export const START_POINT: StartPoint = { mapId: MAP.mossvale, pos: { ...ZONE_SPOTS.start }, facing: r3(angleTo(ZONE_SPOTS.start, ZONE_SPOTS.towerFar)) };
/** +spawn: the town square in front of Elder Rowan (save migrations send displaced heroes here). */
export const TOWN_POINT: StartPoint = { mapId: MAP.mossvale, pos: { ...ZONE_SPOTS.townArrive }, facing: r3(angleTo(ZONE_SPOTS.townArrive, ZONE_SPOTS.elder)) };

/** Every Mossvale open map (the one zone map). */
export const MOSSVALE_FIELD_MAPS: DungeonDef[] = [mossvale];

/** Phase 1 map ids merged into the zone (saves on them move to the zone's town). */
export const LEGACY_FIELD_MAPS: readonly string[] = [MAP.mossvaleTown, MAP.sunmeadow, MAP.bramblewood];
